import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import Tooltip from '@hyphengroup/cms-components/components/Tooltip';
import classNames from 'classnames';
import React from 'react';
import { SharedAttributeData } from '../../helpers/productRecommendation';
import { MultiProductBlockCardItem } from './types';
import { CmsProduct } from '$/(cms)/_modules/cmsService/types';

type MultiProductBlockCardProps = {
  item: MultiProductBlockCardItem;
  index: number;
  getBadgeCopy: (product: CmsProduct, overrideBadge?: string) => string;
  getSharedAttributeData: (product: CmsProduct) => SharedAttributeData;
  renderApplyButton: (
    product: CmsProduct,
    index: number,
    fullWidth?: boolean,
  ) => React.ReactNode;
  renderViewDetailsButton: (
    product: CmsProduct,
    index: number,
    fullWidth?: boolean,
  ) => React.ReactNode;
};

export const MultiProductBlockCard: React.FC<MultiProductBlockCardProps> = ({
  item,
  index,
  getBadgeCopy,
  getSharedAttributeData,
  renderApplyButton,
  renderViewDetailsButton,
}) => {
  const { product, override_badge } = item;
  const badgeCopy = getBadgeCopy(product, override_badge);
  const sharedAttr = getSharedAttributeData(product);

  const viewDetailsButton = renderViewDetailsButton(product, index, true);

  const sharedAttribute = sharedAttr.value ? (
    <div className="flex items-center gap-spacing/gap/x-xs">
      <p className="mhg-alpha-typography-bullet-2 text-text/primary">
        {sharedAttr.value}
      </p>
      {sharedAttr.tooltip && (
        <Tooltip
          placement="top"
          infoClass="!size-[--shapes-icon-size-sm] text-text/disabled"
          content={sharedAttr.tooltip}
        />
      )}
    </div>
  ) : null;

  return (
    <article
      className="flex h-full flex-col gap-spacing/gap/soft-12px"
      data-component="multi-product-block-card"
      data-product-id={product.uid ?? product.entryUID}
    >
      {badgeCopy && (
        <div className="rounded-radius/button px-spacing/padding/soft-8px py-spacing/padding/soft-4px bg-fill/primary-light">
          <div className="mhg-alpha-typography-bullet-2 text-fill/primary-dark">
            {badgeCopy}
          </div>
        </div>
      )}

      <div className="mt-auto flex items-center gap-spacing/gap/x-sm md:flex-col md:items-start lg:flex-row lg:items-center">
        {product.product_image && (
          <CmsImage
            src={product.product_image}
            alt={product.product_name || 'Product image'}
            width={230}
            sizes={{ _: 230 }}
            height={127}
            loading="lazy"
            className="h-14 w-24 shrink-0 rounded-radius/button object-contain md:h-35 md:w-full lg:h-14 lg:w-24"
          />
        )}

        <div className="min-w-0 flex-1">
          {/*
            line-clamp-2 caps the name at 32px: `mhg-alpha-typography-caption`
            is 12px/16px, so two lines is exactly the max height the design
            specifies for this field (CMS-1773, Kimchi Yim). Matters at the
            sidebar's fixed 346px, which is narrower than the previous
            flexible ~398/~437px.
          */}
          <h3 className="mhg-alpha-typography-caption line-clamp-2 text-text/secondary-lighter">
            {product.product_name}
          </h3>
          {sharedAttr.value && (
            <div className="mt-spacing/padding/soft-4px md:mt-spacing/padding/soft-8px lg:mt-spacing/padding/soft-4px">
              {sharedAttribute}
            </div>
          )}
        </div>
      </div>

      <div
        className={classNames(
          'grid gap-spacing/gap/x-xs md:flex md:flex-col lg:grid',
          viewDetailsButton
            ? 'grid-cols-2 lg:grid-cols-2'
            : 'grid-cols-1 lg:grid-cols-1',
        )}
      >
        {/*
          The View Details wrapper is only emitted when the button renders:
          products with no PDP modal content hide it, and an empty grid child
          would still hold its column, pushing the full-width Apply button into
          half the card (CMS-1601).
        */}
        {viewDetailsButton && (
          <div className="md:order-2 lg:order-none">{viewDetailsButton}</div>
        )}
        <div className="md:order-1 lg:order-none">
          {renderApplyButton(product, index, true)}
        </div>
      </div>
    </article>
  );
};
